import React, { useEffect, useRef, useState } from 'react';
import PhysicsLayout from './PhysicsLayout';

const VibrationBench = () => {
  const [m, setM] = useState(2);
  const [k, setK] = useState(180);
  const [c, setC] = useState(4);
  const canvasRef = useRef(null);

  const wn = Math.sqrt(k / m);
  const z = c / (2 * Math.sqrt(k * m));
  const regime = z < 1 ? 'underdamped — it rings' : z < 1.02 ? 'critical' : 'overdamped — it creeps';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const t0 = performance.now();
    let raf;
    const draw = (now) => {
      const t = (now - t0) / 1000;
      const w = canvas.width;
      const h = canvas.height;
      let x = 0;
      if (z < 1) {
        const wd = wn * Math.sqrt(1 - z * z);
        x = Math.exp(-z * wn * t) * Math.cos(wd * t);
      } else {
        x = Math.exp(-wn * t);
      }
      ctx.fillStyle = '#0b1020';
      ctx.fillRect(0, 0, w, h);
      const y0 = h / 2;
      const y = y0 + x * 70;
      ctx.strokeStyle = 'rgba(167,139,250,0.45)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(w / 2, 16);
      const coils = 10;
      for (let i = 0; i <= coils; i += 1) {
        const yy = 16 + ((y - 28 - 16) * i) / coils;
        const xx = w / 2 + ((i % 2 === 0 ? 1 : -1) * 14);
        ctx.lineTo(xx, yy);
      }
      ctx.lineTo(w / 2, y - 28);
      ctx.stroke();
      ctx.fillStyle = '#c4b5fd';
      ctx.fillRect(w / 2 - 28, y - 28, 56, 36);
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [m, k, c, wn, z]);

  return (
    <PhysicsLayout
      kicker="Bench 4 · the one engineers actually fight"
      title="Mass-spring-damper"
      lead="Bump it and watch. Damping ratio ζ is the whole conversation: ring, settle, or crawl. NVH, stages, robots, packages."
    >
      <div className="pp-split">
        <div>
          <label className="pp-field">m {m.toFixed(1)} kg
            <input type="range" min="0.4" max="8" step="0.1" value={m} onChange={(e) => setM(+e.target.value)} />
          </label>
          <label className="pp-field">k {k} N/m
            <input type="range" min="40" max="500" value={k} onChange={(e) => setK(+e.target.value)} />
          </label>
          <label className="pp-field">c {c.toFixed(1)} N·s/m
            <input type="range" min="0" max="40" step="0.5" value={c} onChange={(e) => setC(+e.target.value)} />
          </label>
        </div>
        <div>
          <div className="pp-stage"><canvas ref={canvasRef} width={520} height={260} /></div>
          <div className="pp-readout" style={{ marginTop: '0.8rem' }}>
            <p className="pp-eq">ωₙ = √(k/m) · ζ = c / (2√(km))</p>
            <strong>ζ = {z.toFixed(2)}</strong>
            <p className="pp-note">{regime}. Natural frequency {wn.toFixed(1)} rad/s ({(wn / (2 * Math.PI)).toFixed(2)} Hz). Add damping until the ring dies — that is a design choice, not a decoration.</p>
          </div>
        </div>
      </div>
    </PhysicsLayout>
  );
};

export default VibrationBench;
